import { useEffect, useMemo, useState } from 'react'
import { observer } from 'mobx-react-lite'
import classNames from 'classnames'
import { ArrowRight, NavArrowDown, Sparks } from 'iconoir-react'
import { AliasForm } from 'app/components/mapping/AliasForm'
import { ChannelMappingForm } from 'app/components/mapping/ChannelMappingForm'
import { Button } from 'app/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover'
import { useCheckPermission } from 'app/hooks/useCheckPermission'
import { showModal } from 'app/lib/modal'
import { useChannelMappingStore, useUtmAliasStore } from 'app/stores/StoreContext'
import { AttributionRowType, CleanupSuggestion } from 'app/types/attribution'
import { buildCleanupSuggestions } from 'lib/attributionCleanup'

const CONFIG_PAGE = { pagination: { limit: 200 } }

const VISIBLE_ROWS = 8

export default observer(function DataCleanupNotice({
  sourceRows,
  mediumRows,
  sourceMediumRows,
}: {
  sourceRows?: AttributionRowType[]
  mediumRows?: AttributionRowType[]
  sourceMediumRows?: AttributionRowType[]
}) {
  const aliasStore = useUtmAliasStore()
  const channelMappingStore = useChannelMappingStore()
  const checkPermission = useCheckPermission()
  const canEditMapping = checkPermission([`org:settings:edit-mapping`, `settings:mapping:edit`])
  const [open, setOpen] = useState(false)

  useEffect(() => {
    aliasStore.fetchList(CONFIG_PAGE)
    channelMappingStore.fetchList(CONFIG_PAGE)
  }, [])

  const aliases = aliasStore.hits
  const channelMappings = channelMappingStore.hits

  const suggestions = useMemo(
    () =>
      buildCleanupSuggestions({
        sourceRows: sourceRows ?? [],
        mediumRows: mediumRows ?? [],
        sourceMediumRows: sourceMediumRows ?? [],
        aliases,
        channelMappings,
      }),
    [sourceRows, mediumRows, sourceMediumRows, aliases, channelMappings],
  )

  // Counted over the same rows the fixes come from, so the headline and the list cannot disagree.
  const { unmappedLeads, totalLeads } = useMemo(
    () =>
      (sourceMediumRows ?? []).reduce(
        (totals, row) => ({
          unmappedLeads: totals.unmappedLeads + (row.channel === `Unknown` ? row.leads : 0),
          totalLeads: totals.totalLeads + row.leads,
        }),
        { unmappedLeads: 0, totalLeads: 0 },
      ),
    [sourceMediumRows],
  )

  if (suggestions.length === 0) return null

  const openFix = (suggestion: CleanupSuggestion) => {
    setOpen(false)
    // Neither form reports back, so refetching on close lets a saved alias or mapping retire its
    // own row through the real data, and a cancelled modal changes nothing.
    const onClose = () => {
      aliasStore.fetchList(CONFIG_PAGE, { requestPolicy: `network-only` })
      channelMappingStore.fetchList(CONFIG_PAGE, { requestPolicy: `network-only` })
    }

    if (suggestion.kind === `map`) {
      showModal(
        <ChannelMappingForm
          channel={{ source: suggestion.source, medium: suggestion.medium }}
          isDefaultChannel={false}
        />,
        {
          title: `Map ${suggestion.source} / ${suggestion.medium} to a channel`,
          isFullScreen: false,
          removePadding: true,
          useRadix: true,
          onClose,
        },
      )
      return
    }

    showModal(
      <AliasForm utmAlias={{ original: suggestion.original, alias: suggestion.alias }} type={suggestion.type} />,
      {
        title: suggestion.kind === `merge` ? `Merge duplicate ${suggestion.type}` : `Rename ${suggestion.type}`,
        isFullScreen: false,
        removePadding: true,
        useRadix: true,
        onClose,
      },
    )
  }

  const groups = [
    {
      key: `merge`,
      label: `Spelled more than one way`,
      hint: `The same thing is being counted twice.`,
      rows: suggestions.filter((suggestion) => suggestion.kind === `merge`),
    },
    {
      key: `rename`,
      label: `Known by another name`,
      hint: `Rename these and they fall into a channel on their own.`,
      rows: suggestions.filter((suggestion) => suggestion.kind === `rename`),
    },
    {
      key: `map`,
      label: `No channel yet`,
      hint: `Pick the channel each pairing belongs to.`,
      rows: suggestions.filter((suggestion) => suggestion.kind === `map`),
    },
  ].filter((group) => group.rows.length > 0)

  const share = totalLeads > 0 ? Math.round((unmappedLeads / totalLeads) * 100) : 0
  const fixCount = suggestions.length

  return (
    <div className="mt-6 flex w-full max-w-[1680px] items-center gap-3 rounded-md border border-default bg-surface-raised py-2.5 pl-3 pr-2.5 shadow-elevation-1">
      <Sparks className="h-4 w-4 shrink-0 text-blue-600" strokeWidth={2} />
      <p className="flex-1 text-body-sm leading-5 text-default">
        <span className="font-medium text-strong">Let&apos;s clean up your attribution data.</span>{' '}
        {unmappedLeads > 0 ? (
          <>
            <span className="font-medium text-strong">
              {unmappedLeads.toLocaleString()} {unmappedLeads === 1 ? `lead` : `leads`}
              {share > 0 && ` (${share}%)`}
            </span>{' '}
            {unmappedLeads === 1 ? `isn't` : `aren't`} landing in a channel, and there{` `}
            {fixCount === 1 ? `is 1 quick fix` : `are ${fixCount} quick fixes`} for it.
          </>
        ) : (
          <>{fixCount === 1 ? `One label is` : `${fixCount} labels are`} being counted more than once.</>
        )}
      </p>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button variant="subtle" size="xs" className="-my-0.5">
            Clean up
            <NavArrowDown className="h-3.5 w-3.5 text-minimal" strokeWidth={2} />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="w-[30rem] max-w-[calc(100vw-2rem)] gap-0 overflow-hidden rounded-lg border border-default bg-surface-raised p-0 shadow-elevation-4 ring-0"
        >
          <div className="max-h-96 overflow-y-auto">
            {groups.map((group, index) => (
              <div key={group.key}>
                {/* Tinted because these pin while their rows scroll under them. */}
                <div
                  className={classNames(
                    `sticky top-0 z-10 border-b border-default bg-surface-subtle px-3 py-2`,
                    index > 0 && `border-t`,
                  )}
                >
                  <div className="flex items-center gap-2">
                    <span className="text-ui-xs font-semibold uppercase tracking-wide text-default">{group.label}</span>
                    <span className="rounded-full bg-surface-raised px-1.5 text-ui-xs font-medium text-subtle ring-1 ring-default">
                      {group.rows.length}
                    </span>
                  </div>
                  <p className="mt-0.5 text-ui-xs text-subtle">{group.hint}</p>
                </div>
                <ul className="py-1">
                  {group.rows.slice(0, VISIBLE_ROWS).map((suggestion) => (
                    <li
                      key={
                        suggestion.kind === `map`
                          ? `map:${suggestion.source} / ${suggestion.medium}`
                          : `${suggestion.kind}:${suggestion.type}:${suggestion.original}`
                      }
                      className="flex items-center justify-between gap-3 px-3 py-1.5"
                    >
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5 text-body-sm text-strong">
                          {suggestion.kind === `map` ? (
                            <span className="truncate">
                              {suggestion.source} <span className="text-minimal">/</span> {suggestion.medium}
                            </span>
                          ) : (
                            <>
                              <span className="truncate line-through decoration-trn-300">{suggestion.original}</span>
                              <ArrowRight className="h-3.5 w-3.5 shrink-0 text-minimal" strokeWidth={2} />
                              <span className="truncate font-medium">{suggestion.alias}</span>
                            </>
                          )}
                        </div>
                        <p className="text-ui-xs text-subtle">
                          {suggestion.leads.toLocaleString()} {suggestion.leads === 1 ? `lead` : `leads`}
                          {suggestion.kind === `rename` && suggestion.resultingChannel
                            ? ` · lands in ${suggestion.resultingChannel}`
                            : ``}
                        </p>
                      </div>
                      {canEditMapping && (
                        <Button variant="white" size="xs" className="shrink-0" onClick={() => openFix(suggestion)}>
                          {suggestion.kind === `map` ? `Map` : suggestion.kind === `merge` ? `Merge` : `Rename`}
                        </Button>
                      )}
                    </li>
                  ))}
                  {group.rows.length > VISIBLE_ROWS && (
                    <li className="px-3 py-1.5 text-ui-xs text-minimal">and {group.rows.length - VISIBLE_ROWS} more</li>
                  )}
                </ul>
              </div>
            ))}
          </div>
          <p className="border-t border-subtle px-3 py-2 text-ui-xs text-minimal">
            {canEditMapping
              ? `Fixes apply to past and future leads. The numbers here update once the data reprocesses.`
              : `Someone with mapping access can apply these.`}
          </p>
        </PopoverContent>
      </Popover>
    </div>
  )
})
